/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { atom, createStore } from "jotai/vanilla";
import type { ProcolorRoutingMode } from "./procolor_routing";
import { DEFAULT_PUFF_VALUES, LETTER_DEFAULTS } from "./common/constants";
import ErrorTypes from "./error-types";
import type {
  ArtworkItem,
  ArtworkHistoryItem,
  BogoPromotionData,
  PuffConfig,
  SimulationArtwork,
  SpangleConfig,
  StorePriceSummary
} from "./types";

const DEFAULT_SIMULATION_ARTWORK: SimulationArtwork = {
  ...LETTER_DEFAULTS,
  widthEdited: false,
  isSimulated: true,
  id: "simulation",
  style: null,
  height: null,
  color: null,
  quantity: 1,
  quantities: {},
  sizes: {},
  prices: {},
  enhancementNote: "",
  enhancementFeedbackSubmitted: false,
  enhancementRatingSubmitted: false,
  enhancementSatisfied: null,
  // MN-4778: simulation starts empty too. See vue-product-details-dtf/store.js
  // for rationale.
  sizesAndQuantities: [],
  enhancementTier: "full_enhancements"
};

export const jotaiStore = createStore();

export const showModalAtom = atom(false);

export const showArtworkHistoryModalAtom = atom(false);

export const variantIdAtom = atom<number | null>(null);

export const productIdAtom = atom<number | null>(null);

export const catalogNumberAtom = atom<string | null>(null);

export const printAsIsAtom = atom(false);

export const neckLabelAtom = atom(false);

export const dtfMaxWidthAtom = atom<number | null>(null);

export const dtfMaxHeightAtom = atom<number | null>(null);

export const dtfMinWidthAtom = atom<number | null>(null);

export const dtfMinHeightAtom = atom<number | null>(null);

export const pdpTypeAtom = atom<string | null>(null);

export const preCutAtom = atom(false);

export const productSlugAtom = atom("");

export const showCTAAtom = atom(false);

export const favoritesAtom = atom<number[]>([]);

export const stickerAtom = atom<Record<string, unknown> | null>(null);

export const currentStoreAtom = atom<Record<string, unknown> | null>(null);

export const allowForceBgRemoveAtom = atom(false);

export const canvaReviewerAtom = atom<unknown>(null);

export const unitPriceAtom = atom<number | null>(null);

export const printingServiceNameAtom = atom<string | null>(null);

export const cachedArtworkPricesAtom = atom<Record<string, unknown>>({});

export const dtfCategoryAtom = atom<string | null>(null);

export const spanglesAtom = atom<unknown[]>([]);

export const canvaDtfEnabledStateAtom = atom(false);

export const showMultiSizeArtworkAtom = atom(false);

export const enablePreCutAtom = atom(false);

export const preCutPriceAtom = atom(0);

export const preCutEnabledAtom = atom(false);

export const preCutDurationAtom = atom<string | null>(null);

export const preCutUpsellThresholdAtom = atom(0);

export const loadFromConfigAtom = atom(true);

export const artworksStateAtom = atom<ArtworkItem[]>([]);

export const artworkHistoryAtom = atom<ArtworkHistoryItem[]>([]);

export const artworkHistoryPaginationAtom = atom<{ page: number }>({
  page: 1
});

export const summaryAtom = atom<StorePriceSummary>({
  orderTotal: "0.0",
  productCartQuantity: 0
});

export const bogoPromotionDataAtom = atom<BogoPromotionData>({
  applicable: false
});

export const simulationAtom = atom<SimulationArtwork>({ ...DEFAULT_SIMULATION_ARTWORK });

export const fileToRemoveAtom = atom<number | null>(null);

export const showDiscardModalAtom = atom(false);

export const discardedAtom = atom<Date | null>(null);

export const fetchControllerAtom = atom<AbortController | null>(null);

export const useDefaultValuesAtom = atom(false);

export const showFilesErrorAtom = atom(false);

export const replaceArtworkAtom = atom(false);

export const errorTypesAtom = atom<typeof ErrorTypes>(ErrorTypes);

export const isRetryingAtom = atom(false);

export const allRetryFailedAtom = atom(false);

export const localeAtom = atom("en-US");

export const experimentsAtom = atom<Record<string, number>>({});

export const minStickerQuantityAtom = atom(10);

export const userTypeAtom = atom("guest_user");

export const bogoEligibilityAtom = atom<{ visible: boolean; reason: string | null }>({
  visible: false,
  reason: null
});

export const bogoMaxDimensionLabelAtom = atom('12"x12"');

export const promotionResolutionAtom = atom<{ winner: string }>({ winner: "none" });

export const transferPdpBackButtonDetectionAtom = atom(false);

export const spangleConfigAtom = atom<SpangleConfig>({
  spangleAlgorithms: [],
  isSpangle: false,
  spangleColors: []
});

export const sizeErrorTypesAtom = atom<string[]>([
  ErrorTypes.INVALID_WIDTH.toString(),
  ErrorTypes.INVALID_HEIGHT.toString(),
  ErrorTypes.NEGATIVE_VALUES.toString(),
  ErrorTypes.MIN_PRINT_SIZE.toString(),
  ErrorTypes.MAX_PRINT_SIZE.toString(),
  ErrorTypes.UPSCALE_THRESHOLD_EXCEEDED.toString()
]);

export const isMobileAtom = atom(false);

export const enableMobileFooterCTAAtom = atom(false);

export const dtfChannelUuidAtom = atom<string | null>(null);

export const isEnhancementLoadingAtom = atom(false);

// Guards against duplicate line items when the user rapid-fires Add to Cart
// before the first POST /api/cart/dtf_add completes.
export const isSubmittingAtom = atom(false);

export const actionCableOptionsAtom = atom<Record<string, unknown>>({});

export const actionCableConnectedAtom = atom(false);

export const stickerQrScannedAtom = atom(false);

export const cableAtom = atom<unknown>(null);

export const businessLineAtom = atom<string | null>(null);

export const isPuffAtom = atom(false);

export const puffAtom = atom<PuffConfig>({ ...DEFAULT_PUFF_VALUES });

export const isProColorAtom = atom(false);

export const cmykSoftProofEnabledAtom = atom(false);

export const isStickerAtom = atom(false);

export const isDieCutStickerAtom = atom(false);

export const isStickerSampleAtom = atom(false);

export const isJiffyMadeAtom = atom(false);

export const printReadyInfoConfigAtom = atom<{ show: boolean; expanded: boolean }>({
  show: false,
  expanded: false
});

export const loadingAtom = atom<{ removeBg: boolean }>({
  removeBg: false
});

export const isJerseyNumbersAtom = atom(false);

export const isLettersAtom = atom(false);

export const letterPreviewMessagesAtom = atom<Record<string, unknown>>({});

export const artworkBackgroundAtom = atom("transparent");

export const backgroundTypeAtom = atom("checkered");

export const stickersStyleOptionsAtom = atom(false);

export const letterPreviewLoadingAtom = atom(false);

export const isGoldFoilAtom = atom(false);

export const forcePdpTransfersRedrawAtom = atom(false);

export const pinBasedRedrawEnabledAtom = atom(false);

export const pinBasedRedrawEscapedAtom = atom(false);

export const enableRedrawAtom = atom(false);

export const isPricingLoadingAtom = atom(false);

export const enhancementNoteAtom = atom("");

export const puffMaxImageCountAtom = atom(1);

export const previewCanvasSizeAtom = atom<{ width: number; height: number }>({
  width: 0,
  height: 0
});

export const gangSheetBuilderAtom = atom<unknown>(null);

export const showGangSheetBuilderAtom = atom(false);

export const generatedGangSheetsAtom = atom<unknown[]>([]);

export const gangSheetBuilderShopIdAtom = atom<string | null>(null);

export const showGangSheetBuilderFeatureAtom = atom(false);

export const isGangSheetBuilderAtom = atom(false);

export const gangSheetBuilderVariantsAtom = atom<unknown[]>([]);

export const gangSheetBuilderSettingsAtom = atom<Record<string, unknown>>({});

export const gangSheetBuilderSizesAtom = atom<unknown[]>([]);

export const isDtfSheetAtom = atom(false);

export const gangSheetBulkPriceAtom = atom<Record<string, unknown>>({});

export const storeCreditInfoAtom = atom<Record<string, unknown>>({});

export const presetMockupColorAtom = atom<string[]>(["#E1E1E1"]);

export const gangSheetBuilderCarouselsAtom = atom<Record<string, unknown>>({});

export const updatePricingAbortSignalAtom = atom<AbortController | null>(null);

export const preloadedDesignIdAtom = atom<string | null>(null);

export const reorderEnabledAtom = atom(false);

export const validateArtworkStartTimeAtom = atom<Record<string, number>>({});

export const validateArtworkStartEventIntervalIDAtom = atom<
  Record<string, ReturnType<typeof setInterval>>
>({});

// MN-4782: per-artwork flags for stuck-enhancement UX. Set on the 60s
// IMAGE_UPLOAD_ENHANCEMENTS_STUCK_THRESHOLD to surface the amber notification
// with Retry / Upload-different actions.
export const enhancementStuckAt60sAtom = atom<Record<string, boolean>>({});
// Set on the 3-min transfers-polling-service MAX_POLLING_DURATION to render the
// timeout modal and keep Add-to-Cart disabled until the artwork is re-uploaded.
export const enhancementTimedOutAtom = atom<Record<string, boolean>>({});

export const paidExpertEditAddonEnabledAtom = atom(false);

export const gangSheetCatalogNumbersAtom = atom<string[]>([]);

export const customerReviewOnRedrawsAtom = atom(false);

export const customerReviewOnTransferRedrawsAtom = atom(false);

export const klaviyoSmsTransactionalSubscribedAtom = atom(false);

export const smsTransactionalSubscriptionPdpEnabledAtom = atom(false);

export const smsTransactionalPhoneAtom = atom("");

export const smsTransactionalSubscribeButtonTextAtom = atom("Subscribe");

export const messagingConsentsAtom = atom<{ smsTransactional: boolean }>({
  smsTransactional: true
});

export const blankItemsCountAtom = atom(0);

export const transfersInCartCountAtom = atom(0);

export const isNewToTransfersAtom = atom<boolean | null>(null);

export const showPinningModalAtom = atom(false);

export const pinningArtworkIndexAtom = atom<number | null>(null);

export const pinningModalSessionIdAtom = atom<string | null>(null);

export const pinningSessionLoadingAtom = atom(false);

export const pendingInitialMessageAtom = atom<unknown>(null);

export const pendingPinningInvalidationAtom = atom<unknown>(null);

export const lastConfirmedConfigSectionAtom = atom<string | null>(null);

// PIP-2758: ProColor Free Upgrade — placeholder atom populated by PIP-2763/2764.
// Proves the procolor_routing.d.ts import path resolves (AC #5).
export const procolorRoutingModeAtom = atom<ProcolorRoutingMode>(null);
